import type { ReactNode } from "react";
import type { MethodologyStep, TessaService } from "../data/methodology";

type IconProps = { readonly className?: string | undefined };

/**
 * Single monoline family for the whole TESSA board: no colour of its own, so
 * every icon takes the brand ink of the block it sits in.
 */
function Frame({ children, className }: { children: ReactNode; className?: string | undefined }) {
  return (
    <svg
      className={className ?? ""}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

export function StepIcon({
  icon,
  className,
}: { readonly icon: MethodologyStep["icon"] } & IconProps) {
  switch (icon) {
    // Sites and context — a located place.
    case "site":
      return (
        <Frame className={className}>
          <path d="M12 21s6-5.6 6-10.2A6 6 0 0 0 6 10.8C6 15.4 12 21 12 21Z" />
          <circle cx="12" cy="10.6" r="2.4" />
        </Frame>
      );
    // Identifying ecosystem services — the living asset itself.
    case "services":
      return (
        <Frame className={className}>
          <path d="M4 20c0-8.4 5.2-14 16-16 0 11.2-6.4 16-16 16Z" />
          <path d="M4.5 19.5C8 15.5 12 12 20 4" />
        </Frame>
      );
    // Framing the question — a target to hit.
    case "problem":
      return (
        <Frame className={className}>
          <circle cx="12" cy="12" r="8" />
          <circle cx="12" cy="12" r="3.4" />
          <path d="M12 2v2.4M12 19.6V22M2 12h2.4M19.6 12H22" />
        </Frame>
      );
    // Choosing the method — settings to dial in.
    case "method":
      return (
        <Frame className={className}>
          <path d="M3 7h11M18 7h3" />
          <circle cx="16" cy="7" r="2" />
          <path d="M3 12h5M12 12h9" />
          <circle cx="10" cy="12" r="2" />
          <path d="M3 17h13M20 17h1" />
          <circle cx="18" cy="17" r="2" />
        </Frame>
      );
    // Collecting data — the field record sheet.
    case "data":
      return (
        <Frame className={className}>
          <rect x="4" y="3.5" width="16" height="17" rx="2" />
          <path d="M9 3.5h6V6H9z" />
          <path d="M8.5 16.5v-2.8M12 16.5v-5.6M15.5 16.5v-4.2" />
        </Frame>
      );
    // Analysing and sharing — a rising read.
    case "results":
      return (
        <Frame className={className}>
          <path d="M3.5 3.5v17h17" />
          <path d="M7 15.5l3.6-4.2 3 2.4 5.4-6.2" />
          <path d="M15.4 7.5H19v3.6" />
        </Frame>
      );
    default:
      return null;
  }
}

export function ServiceIcon({
  icon,
  className,
}: { readonly icon: TessaService["icon"] } & IconProps) {
  switch (icon) {
    case "climate":
      return (
        <Frame className={className}>
          <path d="M17 14H7a3.5 3.5 0 0 1 0-7 5 5 0 0 1 9.6 1.3A3 3 0 0 1 17 14Z" />
          <path d="M12 16.4V21.2M9.2 18.4 12 21.2l2.8-2.8" />
        </Frame>
      );
    case "water":
      return (
        <Frame className={className}>
          <path d="M12 3c3.5 4.6 6 7.7 6 10.6a6 6 0 0 1-12 0C6 10.7 8.5 7.6 12 3Z" />
        </Frame>
      );
    case "wild":
      return (
        <Frame className={className}>
          <path d="M3 9h18l-2.2 10.5H5.2L3 9Z" />
          <path d="M8 9a4 4 0 0 1 8 0" />
        </Frame>
      );
    case "cultivated":
      return (
        <Frame className={className}>
          <path d="M12 21V10.5" />
          <path d="M12 10.5C12 7.2 9.3 4.5 6 4.5c0 3.3 2.7 6 6 6Z" />
          <path d="M12 12.5c0-3.3 2.7-6 6-6 0 3.3-2.7 6-6 6Z" />
        </Frame>
      );
    // A tent: nature-based leisure, without reading as a landscape.
    case "recreation":
      return (
        <Frame className={className}>
          <path d="M12 4 3 20h18L12 4Z" />
          <path d="M12 4v16" />
          <path d="M8 20l4-6 4 6" />
        </Frame>
      );
    // A rimmed vessel: heritage and craft, with a silhouette the droplet can't
    // be confused with at icon size.
    case "cultural":
      return (
        <Frame className={className}>
          <ellipse cx="12" cy="5.6" rx="5.6" ry="2.2" />
          <path d="M6.4 5.6c0 2.9-1.6 4.3-1.6 7 0 3.5 3.2 6 7.2 6s7.2-2.5 7.2-6c0-2.7-1.6-4.1-1.6-7" />
          <path d="M7.4 12.6h9.2" />
        </Frame>
      );
    case "pollination":
      return (
        <Frame className={className}>
          <circle cx="12" cy="12.4" r="2.2" />
          <circle cx="12" cy="6.6" r="2.8" />
          <circle cx="17.3" cy="10.5" r="2.8" />
          <circle cx="15.3" cy="16.8" r="2.8" />
          <circle cx="8.7" cy="16.8" r="2.8" />
          <circle cx="6.7" cy="10.5" r="2.8" />
        </Frame>
      );
    case "coastal":
      return (
        <Frame className={className}>
          <path d="M3 15c2-2 4-2 6 0s4 2 6 0 4-2 6 0" />
          <path d="M3 20c2-2 4-2 6 0s4 2 6 0 4-2 6 0" />
          <path d="M12 11.5V6.5" />
          <path d="M12 8.5c0-2.2 1.8-4 4-4 0 2.2-1.8 4-4 4Z" />
          <path d="M12 8.5c0-2.2-1.8-4-4-4 0 2.2 1.8 4 4 4Z" />
        </Frame>
      );
    default:
      return null;
  }
}

export function TessaLogoMark({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <rect className="nbs-tessa-mark__bg" width="48" height="48" rx="11" />
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth={2.4}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M11 33c0-8.6 5.3-14.4 18-16.4C29 28 22.5 33 11 33Z" />
        <path d="M11.5 32.5C15 28.5 19 25 29 16.6" />
        <path d="M10 39h28" />
      </g>
    </svg>
  );
}
